import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { LS } from "../data/localStoreKeys";

// Display formats
/**
 * Global display date format used outside USA.
 */
export const GLOBAL_DATE_FORMAT = "DD/MM/YYYY";
/**
 * Get the user-facing display date format based on active country.
 * USA uses MM-DD-YYYY; others use DD/MM/YYYY.
 */
export const getDateFormat = () => {
  // USA uses MM-DD-YYYY, others use DD/MM/YYYY
  if (import.meta.env.VITE_COUNTRY === "US") {
    return "MM-DD-YYYY";
  }
  return GLOBAL_DATE_FORMAT;
};

dayjs.extend(customParseFormat);

// Backend/storage canonical format: UTC date-only string
/**
 * Canonical backend/storage format for dates.
 */
export const BACKEND_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Normalize a given Date to local midnight (00:00:00.000).
 */
export function normalizeToMidnight(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Parse a backend-formatted date string (YYYY-MM-DD) into a Date at local midnight.
 */
export function parseBackendDateToDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const parsed = dayjs(dateString, BACKEND_DATE_FORMAT, true);
  if (!parsed.isValid()) return null;
  return normalizeToMidnight(parsed.toDate());
}

/**
 * Convert an input (backend string, display string, or Date) into a backend format (YYYY-MM-DD).
 */
export function formatDateForBackend(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  const tryBackend = dayjs(date, BACKEND_DATE_FORMAT, true);
  const tryDisplay = dayjs(date, getDateFormat(), true);
  const parsed = tryBackend.isValid() ? tryBackend : tryDisplay.isValid() ? tryDisplay : dayjs(date);
  if (!parsed.isValid()) return null;
  return parsed.format(BACKEND_DATE_FORMAT);
}

/**
 * Convert an input (backend string, display string, or Date) into a user-facing display string.
 */
export function formatDateForDisplay(date: string | Date | null | undefined): string {
  if (!date) return "-";
  // Accept backend format first, then display format, then native
  let parsed = dayjs(date, BACKEND_DATE_FORMAT, true);
  if (!parsed.isValid()) parsed = dayjs(date, getDateFormat(), true);
  if (!parsed.isValid()) parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(getDateFormat()) : "-";
}

/**
 * Get today's date formatted for backend.
 */
export function getTodayBackend(): string {
  return dayjs().format(BACKEND_DATE_FORMAT);
}

/**
 * Read start date from session and normalize to backend format.
 */
export function readStartDateFromSession(): string | null {
  const raw = sessionStorage.getItem(LS.START_DATE);
  const backend = formatDateForBackend(raw);
  return backend;
}

/**
 * Write a date value to session storage using backend format.
 */
export function writeStartDateToSession(date: string | Date): void {
  const backend = formatDateForBackend(date);
  if (backend) sessionStorage.setItem(LS.START_DATE, backend);
}

// Convenience wrappers used across app
/**
 * Format a date to the current display format (for UI).
 */
export const getFormattedDateFromDateString = (date: string | Date) => {
  return dayjs(date).format(getDateFormat());
};

/**
 * Format a date to backend format (YYYY-MM-DD).
 */
export const getIsoDateFromDateString = (date: string | Date) => {
  return dayjs(date).format(BACKEND_DATE_FORMAT);
};

/**
 * Get all Saturdays and Sundays for the next 15 days as display-formatted strings.
 */
export const getSaturdaysAndSundaysOfNext15Days = () => {
  const result: { startDate: string; endDate: string }[] = [];
  let currentDate = dayjs();
  for (let i = 0; i < 15; i++) {
    const dayOfWeek = currentDate.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      const date = dayjs(currentDate).startOf("week").add(dayOfWeek, "day").format(getDateFormat());
      result.push({ startDate: date, endDate: date });
    }
    currentDate = currentDate.add(1, "day");
  }
  return result;
};

/**
 * Default start date for UI: today in display format, unless a future date exists in session.
 */
export const getDefaultStartDate = () => {
  const oldDate = sessionStorage.getItem(LS.START_DATE);
  if (!oldDate) {
    return dayjs().format(getDateFormat());
  }
  const oldDateInFuture = dayjs(oldDate).isAfter(dayjs());
  if (oldDateInFuture) {
    return oldDate;
  }
  return dayjs().format(getDateFormat());
};

/**
 * Parse any supported input and return a display-formatted date string.
 */
export function parseAndFormatDate(date: string | Date | undefined | null): string {
  if (!date) return "-";
  let parsed = dayjs(date, BACKEND_DATE_FORMAT, true);
  if (!parsed.isValid()) parsed = dayjs(date, getDateFormat(), true);
  if (!parsed.isValid()) parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(getDateFormat()) : "-";
}

/**
 * Clamp a date to [min, max] with midnight normalization.
 */
export function clampToBookingWindow(date: Date, min: Date, max: Date): Date {
  const n = normalizeToMidnight(date).getTime();
  const minT = normalizeToMidnight(min).getTime();
  const maxT = normalizeToMidnight(max).getTime();
  const clampedMs = Math.min(Math.max(n, minT), maxT);
  return new Date(clampedMs);
}

// ----- Booking window helpers -----

let prebookDaysMaximum = 21;

/**
 * Set the maximum number of days a start date can be prebooked in advance.
 */
export function setPrebookDaysMaximum(maxDays: number) {
  prebookDaysMaximum = maxDays;
}

/**
 * Calculate the minimum selectable date (yesterday at 00:00 to allow today).
 */
export const calculateMinDate = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(today.setDate(today.getDate() - 1));
};

/**
 * Calculate the maximum selectable date based on prebookDaysMaximum.
 */
export const calculateMaxDate = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(today.setDate(today.getDate() + prebookDaysMaximum - 1));
};

/**
 * Validate the startDate stored in sessionStorage against the booking window.
 * Expects the value to be stored in backend format (YYYY-MM-DD).
 */
export function isValidDate() {
  console.log("Validating date from sessionStorage...");
  const dateStr = sessionStorage.getItem(LS.START_DATE);
  if (!dateStr) return false;
  const parsed = parseBackendDateToDate(dateStr);
  if (!parsed) return false;
  const minDate = calculateMinDate();
  const maxDate = calculateMaxDate();
  return parsed >= minDate && parsed <= maxDate;
}


